/**
 * Data shape vocabulary for `tento/tento-lona-js/sheets`.
 *
 * All date/time fields use strongly-typed `tento-chrono` values. No
 * string dates; wire strings are decoded once, at the encoding
 * boundary (`sheets/.docs/content/rows-wire.md`).
 */

import type { AuthId, CalendarId, EventId, SlotId, TaskId, UserId } from "../ids/ids.ts";
import type { Option } from "./option.ts";
import { DateTime, NaiveDate, SemanticTime, Utc } from "@tento-chrono";
import type { SheetsNetwork } from "../wire/rows-network.ts";
import type { Lisp } from "@tento-lisp/lisp";
import { Json, type JsonObject } from "./json.ts";
import { CellId } from "../ids/ids.ts";
import { deepFrozen } from "./frozen-collections.ts";

/** One attendee of a `CalendarEvent` (the event cell value), as Google Calendar lists attendees. */
export interface Attendee {
  email: string;
  displayName: Option<string>;
  responseStatus: Option<string>;
  self: Option<boolean>;
}

/** An attendee from what a caller knows of one (`Attendee.of`). */
export namespace Attendee {
  /** What `of` takes: the email, and whatever else is known (left out: `null`). */
  export interface Draft {
    readonly email: string;
    readonly displayName?: string;
    readonly responseStatus?: string;
    readonly self?: boolean;
  }

  /** The attendee `draft` describes; a field it leaves out is `null`. */
  export function of(draft: Draft): Attendee {
    return {
      email: draft.email,
      displayName: draft.displayName ?? null,
      responseStatus: draft.responseStatus ?? null,
      self: draft.self ?? null,
    };
  }
}

/**
 * A formula cell's style: the CSS variable → value pairs that
 * `ModelLispLibrary` helpers such as `cell.bg` write during a formula's
 * evaluation, and that renderers apply inline.
 */
export type CellStyle = Map<string, string>;

/** Atomic type of each value on a dataSource. */
export type Dtype = "text" | "number" | "boolean" | "obj";

/**
 * The output shape a source produces (`SourceDescriptor.sourceShape`): one
 * value per date (`scalar`), a series, a tensor, or one value per cell. Not
 * the cell payload's shape (`meta.shape`, e.g. `event/v1`) and not the
 * storage axis (`CellCardinality`). Stored in a row's attributes as
 * `cardinality`.
 */
export type SourceShape = "scalar" | "series" | "tensor" | "per-cell";

/**
 * Physical storage axis — `single` = one cell per date (default),
 * `multi` = many cells per date keyed by `cell.id` (calendar events,
 * scheduling slots, pomodoro sessions).
 */
export type CellCardinality = "single" | "multi";

/**
 * Canonical shapes for cell payloads in `dtype=obj` sources.
 *
 * These interfaces are the target form produced by the
 * `EncodingRegistry` decoders. Wire payloads may arrive in
 * compact form (`{st, et, …}`); the normalizing backend runs
 * the row's declared `encoding` through the registry before
 * cells land in `SourceRowCellData`.
 *
 * The shapes are **not** a descriptor contract — a row's
 * descriptor declares only coarse axes (dtype / cardinality).
 * Provider rows can declare the hydrated shape in row metadata.
 * Rows without server shape metadata can still be recognized by
 * probing through the `ShapeRegistry`. Whether the cells come from
 * user writes, a provider handler, or formula recompute is a
 * *runtime* property of the row (presence of a `:~` lookup-key, a
 * formula expression, etc.), not a descriptor property.
 */
export interface CalendarEvent {
  id: EventId;
  title: string;
  start: DateTime<Utc>;
  end: DateTime<Utc>;

  /**
   * Authorship / provider scoping. Required — the decoder rejects
   * events missing either field (see `event-v1.ts`). Downstream
   * consumers (calendar filters, event-details UI) treat these as
   * the event's identity on a provider; defaulting them to `""`
   * would silently paint broken events into the UI.
   */
  authId: AuthId;
  calendarId: CalendarId;

  // ── Extras consumed by indices / facades ──────────────────────────
  // `null` when the event has none (build one with `CalendarEvent.of`,
  // which leaves out-of-draft fields `null`). Populated by the normalizing
  // backend decoder when cells arrive from a networked source. On the wire
  // a `null` extra is left out (`CalendarEvent.wire`): a stored cell never
  // carries one.

  creatorIsSelf: Option<boolean>;
  eventType: Option<string>;

  /** Full-day events — when set, `start` / `end` represent midnight bounds. */
  startDate: Option<NaiveDate>;
  endDate: Option<NaiveDate>;

  /** Recurrence. `recurrence` is the RRULE string set on a master event;
   *  `recurringEventId` points an instance at its master. */
  recurrence: Option<readonly string[]>;
  recurringEventId: Option<EventId>;

  /** IANA tz identifier declared on the event (Google: `timeZone`). */
  timezone: Option<string>;
  startTimeZone: Option<string>;
  endTimeZone: Option<string>;
  suggestedTimezone: Option<string>;

  /** Classification / hinting. Consumers interpret freely. */
  category: Option<string>;
  suggestedCategory: Option<string>;
  useSuggestedCategory: Option<boolean>;

  /** Freeform fields renderers may consume. Indices ignore them. */
  description: Option<string>;
  location: Option<string>;
  attendees: Option<readonly Attendee[]>;
  conferenceData: Option<Json>;
}

/** An event from what a caller knows of one (`CalendarEvent.of`), and its wire form. */
export namespace CalendarEvent {
  /** What `of` takes: the identity, title and range, and whatever else is known (left out: `null`). */
  export interface Draft {
    readonly id: EventId;
    readonly title: string;
    readonly start: DateTime<Utc>;
    readonly end: DateTime<Utc>;
    readonly authId: AuthId;
    readonly calendarId: CalendarId;
    readonly creatorIsSelf?: boolean;
    readonly eventType?: string;
    readonly startDate?: NaiveDate;
    readonly endDate?: NaiveDate;
    readonly recurrence?: readonly string[];
    readonly recurringEventId?: EventId;
    readonly timezone?: string;
    readonly startTimeZone?: string;
    readonly endTimeZone?: string;
    readonly suggestedTimezone?: string;
    readonly category?: string;
    readonly suggestedCategory?: string;
    readonly useSuggestedCategory?: boolean;
    readonly description?: string;
    readonly location?: string;
    readonly attendees?: readonly Attendee[];
    readonly conferenceData?: Json;
  }

  /** The event `draft` describes; a field it leaves out is `null`. */
  export function of(draft: Draft): CalendarEvent {
    return {
      id: draft.id,
      title: draft.title,
      start: draft.start,
      end: draft.end,
      authId: draft.authId,
      calendarId: draft.calendarId,
      creatorIsSelf: draft.creatorIsSelf ?? null,
      eventType: draft.eventType ?? null,
      startDate: draft.startDate ?? null,
      endDate: draft.endDate ?? null,
      recurrence: draft.recurrence ?? null,
      recurringEventId: draft.recurringEventId ?? null,
      timezone: draft.timezone ?? null,
      startTimeZone: draft.startTimeZone ?? null,
      endTimeZone: draft.endTimeZone ?? null,
      suggestedTimezone: draft.suggestedTimezone ?? null,
      category: draft.category ?? null,
      suggestedCategory: draft.suggestedCategory ?? null,
      useSuggestedCategory: draft.useSuggestedCategory ?? null,
      description: draft.description ?? null,
      location: draft.location ?? null,
      attendees: draft.attendees ?? null,
      conferenceData: draft.conferenceData ?? null,
    };
  }

  /** The name of one of the extras: a field that is `null` when the event has none. */
  export type Extra =
    | "creatorIsSelf"
    | "eventType"
    | "startDate"
    | "endDate"
    | "recurrence"
    | "recurringEventId"
    | "timezone"
    | "startTimeZone"
    | "endTimeZone"
    | "suggestedTimezone"
    | "category"
    | "suggestedCategory"
    | "useSuggestedCategory"
    | "description"
    | "location"
    | "attendees"
    | "conferenceData";
}

/** Every `CalendarEvent.Extra`, in declaration order. */
const CALENDAR_EVENT_EXTRAS: readonly CalendarEvent.Extra[] = [
  "creatorIsSelf",
  "eventType",
  "startDate",
  "endDate",
  "recurrence",
  "recurringEventId",
  "timezone",
  "startTimeZone",
  "endTimeZone",
  "suggestedTimezone",
  "category",
  "suggestedCategory",
  "useSuggestedCategory",
  "description",
  "location",
  "attendees",
  "conferenceData",
];

/**
 * Whether `value` is a canonical event, as the `event.v1` decoder and
 * `CalendarEvent.of` build one: a string `id`, `authId` and `calendarId`, and
 * `DateTime` bounds (a wire event's are the strings `st` / `et`).
 */
function isCanonicalEvent(value: Option<Json | CellValue>): value is CalendarEvent {
  return typeof value === "object" && value != null && !Array.isArray(value) &&
    "id" in value && typeof value.id === "string" &&
    "authId" in value && typeof value.authId === "string" &&
    "calendarId" in value && typeof value.calendarId === "string" &&
    "start" in value && value.start instanceof DateTime &&
    "end" in value && value.end instanceof DateTime;
}

/**
 * A cell value as a write's `data` JSON: `Json.wire`, except that a canonical
 * `CalendarEvent` leaves out its `null` extras. The body a write sends is the
 * one it sent when an event left those fields out, and a stored cell never
 * carries one; any other field the event carries (a decoded cell's
 * undeclared wire keys) rides along. The one encoder the backends and the
 * row write paths send a cell value through.
 */
export function cellValueWire(value: Option<Json | CellValue>): Option<Json> {
  if (!isCanonicalEvent(value)) return Json.wire(value);
  const out: { [key: string]: Json } = {};
  for (const [key, field] of Object.entries(Json.object(Json.wire(value)) ?? {})) {
    if (field === null && CALENDAR_EVENT_EXTRAS.some((extra) => extra === key)) continue;
    out[key] = field;
  }
  return out;
}

/**
 * A weather reading as a cell carries it: temperature, precipitation and when
 * it was observed.
 */
export interface Weather {
  temperature: number;
  precipitation: Option<number>;
  observedAt: DateTime<Utc>;
}

/** A bookable time slot: its range and who booked it (`null`: nobody yet). */
export interface Slot {
  id: SlotId;
  start: DateTime<Utc>;
  end: DateTime<Utc>;
  bookedBy: Option<UserId>;
}

export interface Report {
  title: string;
  body: string;
  model?: string;
  producedAt: DateTime<Utc>;
}

export interface TzClock {
  tz: string;
  offset?: number;
  label?: string;
}

export interface Sticker {
  icon: string;
  color?: string;
  tooltip?: string;
}

export interface LogEntry {
  at: DateTime<Utc>;
  level: "info" | "warn" | "error";
  message: string;
}

/**
 * A task as a cell carries it: its title, status, importance and dates. The
 * rows-side shape; the wire's is `Network.TaskCell` (`task.v1` decodes it).
 */
export interface TaskCellValue {
  id: TaskId;
  title: string;
  status: Option<TaskCellValue.Status>;
  /** How important the task is (the Tasks panel's Priority column shows it). */
  importance: Option<TaskCellValue.Importance>;
  /** Human-facing id (e.g. `T-12`), surfaced in the Tasks panel's ID column. */
  displayId: Option<string>;
  displayNumber: Option<number>;
  scheduledStart: Option<DateTime<Utc>>;
  scheduledEnd: Option<DateTime<Utc>>;
  dueDate: Option<DateTime<Utc>>;
  completedAt: Option<DateTime<Utc>>;
}

/** Types and values that go with `TaskCellValue`: `Draft`, `of`, `Status`, `Importance`. */
export namespace TaskCellValue {
  /** What `of` takes: the id and title, and whatever else is known (left out: `null`). */
  export interface Draft {
    readonly id: TaskId;
    readonly title: string;
    readonly status?: Status;
    readonly importance?: Importance;
    readonly displayId?: string;
    readonly displayNumber?: number;
    readonly scheduledStart?: DateTime<Utc>;
    readonly scheduledEnd?: DateTime<Utc>;
    readonly dueDate?: DateTime<Utc>;
    readonly completedAt?: DateTime<Utc>;
  }

  /** The task `draft` describes; a field it leaves out is `null`. */
  export function of(draft: Draft): TaskCellValue {
    return {
      id: draft.id,
      title: draft.title,
      status: draft.status ?? null,
      importance: draft.importance ?? null,
      displayId: draft.displayId ?? null,
      displayNumber: draft.displayNumber ?? null,
      scheduledStart: draft.scheduledStart ?? null,
      scheduledEnd: draft.scheduledEnd ?? null,
      dueDate: draft.dueDate ?? null,
      completedAt: draft.completedAt ?? null,
    };
  }

  /**
   * A task cell's status. Not the wire's `Network.TaskStatus`
   * (`completed` / `inProgress` / …): `task.v1` keeps a wire status only
   * when it is one of these.
   */
  export type Status = "open" | "in-progress" | "done" | "cancelled";

  export namespace Status {
    /** Every status, in lifecycle order. */
    export const ALL: readonly Status[] = deepFrozen(["open", "in-progress", "done", "cancelled"]);

    /** `raw` as a `Status`, or `null` when it names none. */
    export function parse(raw: string): Option<Status> {
      return ALL.find((status) => status === raw) ?? null;
    }
  }

  /** A task cell's importance, spelled as the wire's `importance` is. */
  export type Importance = "high" | "medium" | "low";
}

/**
 * Opaque-JSON escape used by `dataSource(obj, ..., "opaque")` rows
 * whose payload shape is provider-defined. Narrower than `unknown` —
 * the nesting is a record, not an arbitrary value.
 */
export type OpaqueJson = Readonly<{ readonly [key: string]: CellValue }>;

/**
 * Closed union of every shape a cell can hold. Concrete variant
 * subclasses narrow at read/write boundaries (e.g. `ColumnTextRow.peekText`
 * returns `string | null`). The store layer holds the union; type
 * refinement happens at the variant call site via generic params.
 *
 * Lesson 1.3 (internals §1.3): untyped `Lisp.Atom` cells in lona-js
 * let a "number" row silently hold a string. This union is the fix.
 */
export type CellValue =
  | string
  | number
  | boolean
  | CalendarEvent
  | Weather
  | Slot
  | Report
  | TzClock
  | Sticker
  | LogEntry
  | TaskCellValue
  /**
   * A task as the tasks client's local edit writes it: its snapshot, the
   * wire's `TaskAttributes` with their `DateTime`s, which the task readers
   * read as written until a fetch replaces it with the decoded
   * `TaskCellValue`.
   */
  | SheetsNetwork.TaskAttributes
  | readonly number[]
  /**
   * List-valued cell — the shape a `DynamicTail.source` row holds, and any
   * other JSON list a cell stores (a list of strings, a nested list).
   * `resolveDynamicTailItems` fans a cell's array value out into one
   * child per element (`alias-templates.ts`), and the wire fixtures
   * seed exactly that (`alias-dynamic-tail-cells.sheet.json`), so the
   * union has to admit it or `SourceRow.write` can't write what
   * `RawSheet.hydrate` already installs. With `OpaqueJson`, this makes
   * every wire `Json` value a `CellValue` as it is — no cast, no copy.
   */
  | readonly CellValue[]
  | OpaqueJson
  | null;

/**
 * A JSON value read back as a `CellValue`: every JSON value is one as it
 * is (a string, number, boolean or `null` as itself, an object as
 * `OpaqueJson`, a list as a `CellValue` list), so the box always holds
 * `value` itself — no copy. The typed variants (`Event`, `Weather`, …) carry
 * `DateTime`s, so their JSON reads as `OpaqueJson`; decode those through
 * their row's encoding instead.
 */
export function cellValueFromJson(value: Json): Option<{ readonly value: CellValue }> {
  return { value };
}

/**
 * A formula's result as the `CellValue` it stores: a number as itself (`-0`
 * as `0`), a string or boolean as itself, a list when every element is a
 * value, and an object as the value a formula's builtins produced (a cell
 * they read, an event or reading they built — a formula has no other source
 * of objects). A procedure (a formula that evaluates to a function) is no
 * value: `null`. The one door from a `Lisp.Atom` into a cell.
 */
export function cellValueFromLisp(atom: Lisp.Atom): Option<CellValue> {
  if (typeof atom === "number") return Object.is(atom, -0) ? 0 : atom;
  return isLispCellValue(atom) ? (atom as CellValue) : null;
}

function isLispCellValue(atom: Lisp.Atom): boolean {
  if (typeof atom === "function") return false;
  if (Array.isArray(atom)) return atom.every((item: Lisp.Atom) => isLispCellValue(item));
  return true;
}

/**
 * One point in time for a row. `single`-card cells key on `date`
 * alone; `multi`-card cells add an `id` to disambiguate (e.g.,
 * calendar events, scheduling slots).
 *
 * The cardinality contract — "point-time cells must carry a
 * stable id, single-card range cells must not" — is enforced
 * when a wire cell is decoded, by the `CellEntry.SingleCard`,
 * `CellEntry.MultiCard`, and `CellEntry.Timepoint` decoders
 * below (`from` / `tryFrom` take a wire cell; they are not
 * builders). Read sites then trust the shape:
 *
 * - `CellEntry`             — date-anchored umbrella; `id` is an
 *                             `Option`: `null` on single-card rows,
 *                             set on multi-card range rows.
 * - `CellEntry.Timepoint`   — point-time, always multi-card; `id`
 *                             is **required**, so consumers can read
 *                             `cell.id` without a fallback.
 *
 * Mirrors the server's `tento_lona_connector::Cell` closed-type
 * contract: one cell structure; cardinality decides the key shape
 * (`date` vs `(date, cellId)`), and a point-time cell carries a stable id.
 */
export interface CellEntry {
  /** Partial date — `NaiveDate.Partial` granularity matches `Timescale` (day/week/month/year). */
  readonly date: NaiveDate.Partial;
  /**
   * Set by the multi-card decoder (`CellEntry.MultiCard.from`);
   * `CellEntry.SingleCard.from` sets `null`. A hand-built
   * single-card entry sets `id: null`; never derive a multi-card
   * id with a `?? null` fallback.
   */
  readonly id: Option<CellId>;
  readonly value: CellValue;
  /**
   * The per-cell style bag (`bg`, `bg.N`, …), or `null` for an unstyled
   * cell. Populated by formulas calling `(cell.bg "red")` — the
   * evaluation's `context.style` is snapshotted into this field when
   * non-empty. UI renderers read it as inline
   * CSS variables. See `formula-typed-outputs.md` §3.1.
   */
  readonly style: Option<CellStyle>;
}

/** Types that go with `CellEntry`: `Timepoint`, `SingleCard`, `MultiCard`. */
export namespace CellEntry {
  /**
   * Point-time cell — anchored to a DateTime range. Per the
   * server contract (`tento_lona_connector::Cell::point` + the
   * wire `try_from = "RawCell"` validator), point-time cells
   * always carry an id, so it is **required** here too.
   *
   * `dtr` is generic on its timezone so a downstream consumer can
   * map a `Timepoint<Utc>` (the wire-decoded form) into a
   * `Timepoint<TimezoneRegion>` for rendering without changing
   * the cell's identity.
   */
  export interface Timepoint<Tz extends string = Utc> {
    readonly id: CellId;
    readonly dtr: DateTime.Range<Tz>;
    readonly value: CellValue;
    readonly style: Option<CellStyle>;
  }

  /**
   * Decoders from `SheetsNetwork.SheetCell` to typed entries. Two
   * flavors per variant:
   *
   * - `tryFrom(cell)`: `Option<...>` — returns `null` if the
   *   wire cell violates the variant's invariant. The caller
   *   decides whether to skip / log / route.
   * - `from(cell)`: `...` — throws if the invariant is violated.
   *   Use when the caller has already established the variant
   *   from context (row cardinality, descriptor) and a violation
   *   is a programmer / server bug.
   *
   * No silent `id ?? cell.date` / `?? ""` fallbacks — callers
   * must be explicit about how to handle invariant breaks.
   */
  export namespace SingleCard {
    export function tryFrom(cell: SheetsNetwork.SheetCell): Option<CellEntry> {
      const date = SemanticTime.dateKey(cell.date);
      if (date == null) return null;
      return { date, id: null, value: cell.data ?? null, style: null };
    }

    export function from(cell: SheetsNetwork.SheetCell): CellEntry {
      const out = tryFrom(cell);
      if (out == null) {
        throw new Error(
          `CellEntry.SingleCard.from: cell with date "${cell.date}" did not yield a partial-date key`,
        );
      }
      return out;
    }
  }

  export namespace MultiCard {
    export function tryFrom(cell: SheetsNetwork.SheetCell): Option<CellEntry> {
      const date = SemanticTime.dateKey(cell.date);
      if (date == null) return null;
      if (cell.id == null) return null;
      return {
        date,
        id: cell.id,
        value: cell.data ?? null,
        style: null,
      };
    }

    export function from(cell: SheetsNetwork.SheetCell): CellEntry {
      const out = tryFrom(cell);
      if (out == null) {
        throw new Error(
          `CellEntry.MultiCard.from: multi-card cell missing id or invalid date "${cell.date}" (id=${cell.id ?? "null"})`,
        );
      }
      return out;
    }
  }

  export namespace Timepoint {
    export function tryFrom(cell: SheetsNetwork.SheetCell): Option<Timepoint> {
      if (cell.id == null) return null;
      const start = SemanticTime.dateTime(cell.date);
      if (start == null) return null;
      const end = cell.endDate
        ? SemanticTime.dateTime(cell.endDate) ?? start
        : start;
      return {
        id: cell.id,
        dtr: new DateTime.Range(start, end),
        value: cell.data ?? null,
        style: null,
      };
    }

    export function from(cell: SheetsNetwork.SheetCell): Timepoint {
      const out = tryFrom(cell);
      if (out == null) {
        throw new Error(
          `CellEntry.Timepoint.from: timepoint cell with date "${cell.date}" missing id or unparseable (id=${cell.id ?? "null"})`,
        );
      }
      return out;
    }
  }
}
